import { FC, useContext } from 'react'
import { useIntl } from 'react-intl'

import { Col, Flex, Form, Row, Select, Switch, Typography } from 'antd'
import { useResponsive } from 'antd-style'

import { useQuery } from '@tanstack/react-query'

import { Country } from '../../components/icon/Country'
import { generateSlug } from '../../routes'
import { client } from '../../services/motogp'

import { Context } from './Context'
import { defaultState } from './index'

const ResultsForm: FC = () => {
  const { mobile } = useResponsive()
  const { formatMessage } = useIntl()
  const [form] = Form.useForm()
  const { state, setState } = useContext(Context)
  const seasonsQuery = useQuery({ queryKey: ['seasons'], queryFn: () => client.seasons() })

  const categoriesQuery = useQuery({
    queryKey: ['categories', state.seasonId],
    queryFn: () => client.categories(state.seasonId),
  })

  const eventsQuery = useQuery({
    queryKey: ['events', state.seasonId, state.test ? 'test' : ''].filter(Boolean),
    queryFn: () => client.events(state.seasonId),
  })

  return (
    <Form form={form} onReset={() => setState(defaultState)} labelWrap layout={'inline'}>
      <Row gutter={[mobile ? 16 : 16, 16]} style={{ width: '100%' }} align={'top'} justify={'space-between'}>
        <Col xs={12} lg={4}>
          <Form.Item initialValue={state.seasonId} style={{ width: '100%' }} name={'year'}>
            <Select<string>
              showSearch
              filterOption={(input, option) => (option?.title ?? '').includes(input.toLowerCase())}
              popupMatchSelectWidth={false}
              onChange={(seasonId) => {
                setState((prevState) => ({
                  ...prevState,
                  seasonId,
                  categoryId: '',
                  eventId: '',
                  params: {
                    ...prevState.params,
                    year: seasonsQuery.data?.find((it) => it.id === seasonId)?.year.toString() ?? '',
                  },
                }))
                form.setFieldValue('category', '')
                form.setFieldValue('event', '')
              }}
              placeholder={state.seasonId ? '2025' : formatMessage({ id: 'results.form.season' })}
              defaultActiveFirstOption
              loading={seasonsQuery.isLoading}
              options={seasonsQuery.data?.map((it) => ({
                title: it.year.toString(),
                label: <Typography.Text code>{it.year}</Typography.Text>,
                value: it.id,
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={12} lg={5}>
          <Form.Item initialValue={state.categoryId ?? undefined} style={{ width: '100%' }} name={'category'}>
            <Select
              popupMatchSelectWidth={false}
              loading={categoriesQuery?.isLoading}
              onChange={(categoryId) => {
                setState((prevState) => ({
                  ...prevState,
                  categoryId,
                  params: {
                    ...prevState.params,
                    category: generateSlug(
                      categoriesQuery?.data?.find((it) => it.id === categoryId)?.name.toLowerCase() ?? '',
                    ).replace('tm', ''),
                  },
                }))
              }}
              options={categoriesQuery?.data?.map((it) => ({
                label: <Typography.Text code>{it.name.slice(0, it.name.length - 1)}</Typography.Text>,
                value: it.id,
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item initialValue={state.eventId ?? undefined} style={{ width: '100%' }} name={'event'}>
            <Select<string>
              showSearch
              loading={eventsQuery?.isLoading}
              filterOption={(input, option) => (option?.title?.toLowerCase() ?? '').includes(input.toLowerCase())}
              onChange={(eventId) =>
                setState((prevState) => ({
                  ...prevState,
                  eventId,
                  params: {
                    ...prevState.params,
                    track: generateSlug(eventsQuery?.data?.find((it) => it.id === eventId)?.circuit.name ?? ''),
                  },
                }))
              }
              options={eventsQuery?.data
                ?.filter((it) => it.test === !!state.test)
                ?.map((it) => {
                  const label = (
                    <Flex gap={4} align={'center'}>
                      <Country height={16} country={it.country.iso} />
                      <Typography.Text code>{it.country.iso}</Typography.Text>
                      <Typography.Text ellipsis>{it.circuit.name}</Typography.Text>
                    </Flex>
                  )
                  return {
                    label,
                    value: it.id,
                    title: [it.name, it.circuit.name].join(' '),
                  }
                })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} lg={3}>
          <Form.Item
            label={formatMessage({ id: 'results.form.tests' })}
            initialValue={state.test}
            style={{ width: '100%' }}
            name={'test'}
          >
            <Switch
              checked={state.test}
              onChange={(test) => {
                setState((prevState) => ({ ...prevState, test }))
                form.setFieldValue('event', '')
              }}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  )
}

export { ResultsForm }
